/**
 * Robust CID parser for values read from IndexedDB.
 *
 * `CID.parse()` only handles base32 (`b`), base36 (`k`), and base58btc (`z`/`Q`)
 * multibase prefixes by default.  Stored CIDs may arrive as:
 *   - a CIDv1 base32 string (the normal case, starts with `b`)
 *   - a CIDv0 base58btc string (starts with `Q`)
 *   - a CID encoded with a non-default multibase (e.g. base64 `m`)
 *   - a CID *object* that was structured-cloned by IndexedDB into a plain object
 *     carrying a `.bytes` property
 *   - a raw `Uint8Array` of CID bytes
 *
 * This helper normalises all of those into a proper `CID` instance.
 */
import { CID } from 'multiformats/cid'
import { base64 } from 'multiformats/bases/base64'

/**
 * Normalize a CID-like value that may have lost its prototype through
 * Comlink structured-clone transfer.  Returns a proper CID instance.
 *
 * Use this whenever a CID crosses a worker ↔ main-thread boundary before
 * calling `.toString()`, `.toV1()`, or any other CID method.
 */
export function normalizeCID(value: any): CID {
	const asCid = CID.asCID(value)
	if (asCid) return asCid
	if (value && value.bytes instanceof Uint8Array) return CID.decode(value.bytes)
	if (value instanceof Uint8Array) return CID.decode(value)
	throw new Error(`normalizeCID: cannot reconstruct CID from ${typeof value} (${value?.constructor?.name ?? 'null'})`)
}

export function parseStoredCID(value: any, label?: string): CID {
	if (value == null) throw new Error(`parseStoredCID: null/undefined value${label ? ` (${label})` : ''}`)

	// Already a CID instance — return as-is
	const asCid = CID.asCID(value)
	if (asCid) return asCid

	// Plain object with a `.bytes` property (structured-clone of a CID from IDB)
	if (typeof value === 'object' && !(value instanceof Uint8Array) && value.bytes instanceof Uint8Array) {
		try {
			return CID.decode(value.bytes)
		} catch { /* fall through */ }
	}

	// Uint8Array of raw CID bytes
	if (value instanceof Uint8Array) {
		try {
			return CID.decode(value)
		} catch { /* fall through */ }
	}

	// String — try default parsers first (base32, base36, base58btc), then
	// fall back to base64 which CID.parse does NOT handle without a decoder.
	if (typeof value === 'string') {
		try {
			return CID.parse(value)
		} catch {
			// Try base64 decoder (prefix `m`, `M`, `u`, `U`)
			const first = value[0]
			if (first === 'm' || first === 'M' || first === 'u' || first === 'U') {
				try {
					const bytes = base64.decode(value)
					return CID.decode(bytes)
				} catch { /* fall through */ }
			}
			// As a last resort, try decoding the whole string as base64 CID bytes.
			try {
				const bytes = base64.decode(value)
				return CID.decode(bytes)
			} catch { /* fall through */ }
		}
	}

	throw new Error(
		`parseStoredCID: unable to decode CID from ${typeof value}` +
			(label ? ` (${label})` : '') +
			(typeof value === 'string' ? ` – value starts with "${value.slice(0, 20)}…"` : '') +
			(typeof value === 'object' && value?.constructor?.name ? ` (constructor: ${value.constructor.name})` : ''),
	)
}
